import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE
  || (window.location.hostname === 'localhost' ? 'http://localhost:8080' : '');

const number = (value) => Number(value || 0).toLocaleString();

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).replace('T', ' ');
  return date.toLocaleString();
}

function formatDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '-';
  if (seconds < 1) return '0초';
  if (seconds < 60) return `${Math.round(seconds)}초`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}분 ${s}초` : `${m}분`;
}

// ────────────────────────────────────────────────────────
// ⚙️ 크론식 - UI 바인딩 헬퍼 함수
// ────────────────────────────────────────────────────────
function parseCronToUi(cronExpr) {
  const parts = (cronExpr || '0 0 3 * * *').trim().split(/\s+/);
  const def = { mode: 'daily', intervalValue: 30, intervalUnit: 'minute', hour: '03', minute: '00', dayOfWeek: 'MON' };
  if (parts.length < 6) return def;

  // eslint-disable-next-line no-unused-vars
  const [sec, minPart, hourPart, dayPart, monthPart, dayOfWeekPart] = parts;

  if (minPart.includes('*/')) {
    return {
      mode: 'interval',
      intervalValue: parseInt(minPart.split('*/')[1], 10) || 30,
      intervalUnit: 'minute',
      hour: '00',
      minute: '00',
      dayOfWeek: 'MON'
    };
  }
  if (hourPart.includes('*/')) {
    return {
      mode: 'interval',
      intervalValue: parseInt(hourPart.split('*/')[1], 10) || 1,
      intervalUnit: 'hour',
      hour: '00',
      minute: '00',
      dayOfWeek: 'MON'
    };
  }

  if (dayOfWeekPart !== '*' && dayOfWeekPart !== '?') {
    return {
      mode: 'weekly',
      intervalValue: 30,
      intervalUnit: 'minute',
      hour: String(hourPart).padStart(2, '0'),
      minute: String(minPart).padStart(2, '0'),
      dayOfWeek: dayOfWeekPart.split(',')[0].toUpperCase()
    };
  }

  return {
    mode: 'daily',
    intervalValue: 30,
    intervalUnit: 'minute',
    hour: String(hourPart).padStart(2, '0'),
    minute: String(minPart).padStart(2, '0'),
    dayOfWeek: 'MON'
  };
}

function compileUiToCron(uiState) {
  const { mode, intervalValue, intervalUnit, hour, minute, dayOfWeek } = uiState;
  if (mode === 'interval') {
    if (intervalUnit === 'minute') {
      return `0 */${intervalValue} * * * *`;
    } else {
      return `0 0 */${intervalValue} * * *`;
    }
  } else if (mode === 'weekly') {
    return `0 ${parseInt(minute, 10)} ${parseInt(hour, 10)} * * ${dayOfWeek}`;
  } else {
    return `0 ${parseInt(minute, 10)} ${parseInt(hour, 10)} * * *`;
  }
}

// ────────────────────────────────────────────────────────
// 📅 크론식 기반 가상 캘린더 스케줄 생성 헬퍼
// ────────────────────────────────────────────────────────
function generateScheduleEvents(cronExpr, jobName, dateMode, targetDate = new Date()) {
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length < 6) return [];

  // eslint-disable-next-line no-unused-vars
  const [sec, minPart, hourPart, dayPart, monthPart, dayOfWeekPart] = parts;
  const events = [];

  const isMinInterval = minPart.includes('*/');
  const isHourInterval = hourPart.includes('*/');
  const minVal = isMinInterval ? parseInt(minPart.split('*/')[1], 10) : minPart;
  const hourVal = isHourInterval ? parseInt(hourPart.split('*/')[1], 10) : hourPart;

  const dayOfWeekMap = {
    '0': 0, 'SUN': 0, '1': 1, 'MON': 1, '2': 2, 'TUE': 2,
    '3': 3, 'WED': 3, '4': 4, 'THU': 4, '5': 5, 'FRI': 5, '6': 6, 'SAT': 6
  };

  const getWeekDayNum = (val) => {
    if (val === '*' || val === '?') return null;
    const upper = String(val).toUpperCase();
    return dayOfWeekMap[upper] !== undefined ? dayOfWeekMap[upper] : parseInt(val, 10);
  };

  const targetDayOfWeek = getWeekDayNum(dayOfWeekPart);

  if (dateMode === 'day') {
    if (isMinInterval) {
      const step = minVal;
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += step) {
          events.push({ time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, jobName });
        }
      }
    } else if (isHourInterval) {
      const step = hourVal;
      for (let h = 0; h < 24; h += step) {
        events.push({ time: `${String(h).padStart(2, '0')}:00`, jobName });
      }
    } else {
      if (targetDayOfWeek !== null && targetDate.getDay() !== targetDayOfWeek) return [];
      const hours = hourPart === '*' ? [0] : hourPart.split(',').map(Number);
      const mins = minPart === '*' ? [0] : minPart.split(',').map(Number);
      hours.forEach(h => {
        mins.forEach(m => {
          events.push({ time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, jobName });
        });
      });
    }
  } else if (dateMode === 'week') {
    const currentDay = targetDate.getDay();
    const startOfWeek = new Date(targetDate);
    startOfWeek.setDate(targetDate.getDate() - currentDay);

    for (let i = 0; i < 7; i++) {
      const simDate = new Date(startOfWeek);
      simDate.setDate(startOfWeek.getDate() + i);

      if (targetDayOfWeek !== null && simDate.getDay() !== targetDayOfWeek) continue;

      const dayEvts = generateScheduleEvents(cronExpr, jobName, 'day', simDate);
      dayEvts.forEach(de => {
        events.push({ dateStr: simDate.toISOString().split('T')[0], time: de.time, jobName, dayIndex: i });
      });
    }
  } else if (dateMode === 'month') {
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let d = 1; d <= daysInMonth; d++) {
      const simDate = new Date(year, month, d);

      if (targetDayOfWeek !== null && simDate.getDay() !== targetDayOfWeek) continue;

      const dayEvts = generateScheduleEvents(cronExpr, jobName, 'day', simDate);
      dayEvts.forEach(de => {
        events.push({ dateStr: simDate.toISOString().split('T')[0], time: de.time, jobName, day: d });
      });
    }
  }

  return events;
}

function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem('festival-admin-token') || '');
  const [draftToken, setDraftToken] = useState(token);
  const [authorized, setAuthorized] = useState(false);
  const [message, setMessage] = useState('');
  const [statuses, setStatuses] = useState({});
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [busyType, setBusyType] = useState('');

  const [activeMainTab, setActiveMainTab] = useState('schedule');
  const [schedules, setSchedules] = useState([]);
  
  // 사용자 통계 상태 정의
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsSessions, setAnalyticsSessions] = useState([]);
  const [analyticsSessionTotal, setAnalyticsSessionTotal] = useState(0);
  const [analyticsSessionPage, setAnalyticsSessionPage] = useState(0);
  const [analyticsSessionDate, setAnalyticsSessionDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [analyticsSessionDevice, setAnalyticsSessionDevice] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionEvents, setSessionEvents] = useState([]);
  const [loadingSessionEvents, setLoadingSessionEvents] = useState(false);

  const [calendarMode, setCalendarMode] = useState('day'); // 'day' | 'week' | 'month'
  const [calendarAnchorDate, setCalendarAnchorDate] = useState(new Date());

  const [filterDate, setFilterDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterType, setFilterType] = useState('');
  const [logPage, setLogPage] = useState(0);


  // 4. 스케줄 UI 상세 수정 상태 캐싱
  const [activeEditingJob, setActiveEditingJob] = useState(null); // jobName
  const [editState, setEditState] = useState({
    mode: 'daily',
    intervalValue: 30,
    intervalUnit: 'minute',
    hour: '03',
    minute: '00',
    dayOfWeek: 'MON'
  });

  const client = useMemo(() => axios.create({
    baseURL: `${API_BASE}/api/admin`,
    headers: token ? { 'X-Admin-Token': token } : {},
  }), [token]);

  // 사용자 통계 API 호출 함수 정의
  const fetchAnalyticsSummary = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await client.get('/analytics/summary');
      setAnalyticsSummary(data);
    } catch (e) {
      console.warn('Analytics summary load failed:', e.message);
    }
  }, [client, token]);

  const fetchAnalyticsSessions = useCallback(async (page = 0) => {
    if (!token) return;
    try {
      const { data } = await client.get('/analytics/sessions', {
        params: {
          page,
          size: 15,
          searchDate: analyticsSessionDate,
          deviceType: analyticsSessionDevice === 'all' ? null : analyticsSessionDevice
        }
      });
      setAnalyticsSessions(data.sessions || []);
      setAnalyticsSessionTotal(data.total || 0);
      setAnalyticsSessionPage(page);
    } catch (e) {
      console.warn('Analytics sessions load failed:', e.message);
    }
  }, [client, token, analyticsSessionDate, analyticsSessionDevice]);

  const fetchSessionEvents = useCallback(async (session) => {
    setSelectedSession(session);
    setLoadingSessionEvents(true);
    try {
      const { data } = await client.get(`/analytics/sessions/${session.id}/events`);
      setSessionEvents(data || []);
    } catch (e) {
      setSessionEvents([]);
    } finally {
      setLoadingSessionEvents(false);
    }
  }, [client]);

  const handleRefreshAnalytics = useCallback(async () => {
    await Promise.all([
      fetchAnalyticsSummary(),
      fetchAnalyticsSessions(0)
    ]);
  }, [fetchAnalyticsSummary, fetchAnalyticsSessions]);

  // 메인 탭 변경 시 사용자 통계 데이터 연동
  useEffect(() => {
    if (activeMainTab === 'analytics') {
      fetchAnalyticsSummary();
      fetchAnalyticsSessions(0);
    }
  }, [activeMainTab, fetchAnalyticsSummary, fetchAnalyticsSessions]);

  const saveToken = async () => {
    localStorage.setItem('festival-admin-token', draftToken);
    setToken(draftToken);
  };

  const loadDashboardData = useCallback(async () => {
    if (!token) return;
    try {
      const [ping, statusRes, statsRes, schedulesRes] = await Promise.all([
        client.get('/ping'),
        client.get('/collect/statuses'),
        client.get('/stats/data'),
        client.get('/schedules'),
      ]);
      setAuthorized(Boolean(ping.data.ok));
      setStatuses(statusRes.data || {});
      setStats(statsRes.data || {});
      setSchedules(schedulesRes.data || []);
      setMessage('');
    } catch (error) {
      setAuthorized(false);
      setMessage('관리자 토큰을 확인해주세요.');
    }
  }, [client, token]);

  // 로그 목록만 따로 필터 걸어서 조회하는 함수
  const fetchFilteredLogs = useCallback(async (page = 0) => {
    if (!token) return;
    try {
      const { data } = await client.get(`/logs`, {
        params: {
          page,
          size: 25,
          searchDate: filterDate,
          collectType: filterType
        }
      });
      setLogs(data.logs || []);
      setLogTotal(data.total || 0);
      setLogPage(page);
    } catch (error) {
      setMessage('로그 필터 조회 실패');
    }
  }, [client, token, filterDate, filterType]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // 대형 탭 및 초기 진입 시 로그 조회 자동 연동
  useEffect(() => {
    if (activeMainTab === 'logs') {
      fetchFilteredLogs(0);
    }
  }, [activeMainTab, fetchFilteredLogs]);

  useEffect(() => {
    if (!authorized) return undefined;
    const timer = setInterval(() => {
      client.get('/collect/statuses').then(res => setStatuses(res.data || {})).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [authorized, client]);

  // 스케줄 설정 저장
  const updateSchedule = async (jobName, cron, enabled, description) => {
    try {
      await client.put(`/schedules/${jobName}`, {
        cronExpression: cron,
        enabled,
        description
      });
      setMessage(`${jobName} 스케줄이 실시간 업데이트되었습니다.`);
      await loadDashboardData();
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || '스케줄 변경 실패');
    }
  };

  // 수동 즉시 수집
  const triggerSchedule = async (jobName) => {
    setBusyType(jobName);
    try {
      await client.post(`/schedules/${jobName}/trigger`);
      setMessage(`${jobName} 수집이 즉시 백그라운드 구동되었습니다.`);
      setTimeout(() => loadDashboardData(), 1500);
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || '수집 실행 실패');
    } finally {
      setBusyType('');
    }
  };

  const openLog = async (log) => {
    setSelectedLog(log);
    try {
      const { data } = await client.get(`/logs/${log.id}/lines`);
      setLogLines(data || []);
    } catch (error) {
      setLogLines([{ logLevel: 'ERROR', message: error.message }]);
    }
  };

  // ────────────────────────────────────────────────────────
  // UI 수정 폼 오픈 헬퍼
  // ────────────────────────────────────────────────────────
  const startEditing = (sch) => {
    setActiveEditingJob(sch.jobName);
    setEditState(parseCronToUi(sch.cronExpression));
  };

  const saveEditedCron = async (sch) => {
    const cron = compileUiToCron(editState);
    await updateSchedule(sch.jobName, cron, sch.enabled, sch.description);
    setActiveEditingJob(null);
  };

  // ────────────────────────────────────────────────────────
  // 달력 / 타임라인 시뮬레이션 이벤트 취합
  // ────────────────────────────────────────────────────────
  const calendarEvents = useMemo(() => {
    let list = [];
    schedules.forEach(sch => {
      if (!sch.enabled) return;
      const evts = generateScheduleEvents(sch.cronExpression, sch.jobName, calendarMode, calendarAnchorDate);
      list = list.concat(evts);
    });

    // 시간 순 정렬
    list.sort((a, b) => a.time.localeCompare(b.time));
    return list;
  }, [schedules, calendarMode, calendarAnchorDate]);

  if (!authorized) {
    return (
      <div className="admin-page admin-login">
        <section className="admin-login-box">
          <h1>운영 콘솔</h1>
          <p>관리자 토큰을 입력하면 수집과 로그를 관리할 수 있습니다.</p>
          <input
            type="password"
            value={draftToken}
            onChange={(e) => setDraftToken(e.target.value)}
            placeholder="ADMIN_TOKEN"
            onKeyDown={(e) => { if (e.key === 'Enter') saveToken(); }}
          />
          <button onClick={saveToken}>접속</button>
          {message && <div className="admin-message admin-message-error">{message}</div>}
        </section>
      </div>
    );
  }

  // 캘린더 보조 날짜 계산용 변수
  const yearStr = calendarAnchorDate.getFullYear();
  const monthStr = calendarAnchorDate.getMonth();
  const daysInMonth = new Date(yearStr, monthStr + 1, 0).getDate();
  const firstDayIndex = new Date(yearStr, monthStr, 1).getDay();

  return (
    <div className="admin-page">
      <header className="admin-topbar">
        <div>
          <h1>운영 콘솔</h1>
          <p>수집 스케줄러 시각화 캘린더와 상세 로그 그리드 필터 뷰를 관리합니다.</p>
        </div>
        <div className="admin-topbar-actions">
          <div className="admin-main-tabs">
            <button className={`main-tab-btn ${activeMainTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveMainTab('schedule')}>
              📅 스케줄 및 캘린더 일정
            </button>
            <button className={`main-tab-btn ${activeMainTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveMainTab('logs')}>
              📋 수집 로그 조회
            </button>
            <button className={`main-tab-btn ${activeMainTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveMainTab('analytics')}>
              📊 사용자 통계
            </button>
          </div>
          <button onClick={loadDashboardData} className="btn-refresh">새로고침</button>
          <button onClick={() => { localStorage.removeItem('festival-admin-token'); setToken(''); setAuthorized(false); }} className="btn-logout">토큰 삭제</button>
        </div>
      </header>

      {message && <div className="admin-message">{message}</div>}

      {/* 1️⃣ 스케줄 및 달력 시각화 메인 탭 */}
      {activeMainTab === 'schedule' && (
        <>
          {/* 스케줄 관리 그리드 */}
          <section className="admin-section">
            <div className="admin-section-head">
              <h2>수집 배정 일정표 (스케줄)</h2>
              <span>데이터 수집 주기를 텍스트 크론식 대신 아래 설정 UI로 손쉽게 지정합니다.</span>
            </div>
            <div className="admin-grid-table-container">
              <table className="admin-grid-table">
                <thead>
                  <tr>
                    <th>작업 유형</th>
                    <th>설명</th>
                    <th>스케줄 설정 (UI 형태 변경)</th>
                    <th>크론 코드</th>
                    <th>동작 상태</th>
                    <th style={{ textAlign: 'center' }}>사용 여부</th>
                    <th style={{ textAlign: 'center' }}>즉시 수집</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(sch => {
                    const isEditing = activeEditingJob === sch.jobName;
                    const isBusy = busyType === sch.jobName;

                    return (
                      <tr key={sch.jobName} className={sch.enabled ? 'row-enabled' : 'row-disabled'}>
                        <td><strong className="job-name-highlight">{sch.jobName}</strong></td>
                        <td><span className="job-desc">{sch.description || '-'}</span></td>
                        <td>
                          {isEditing ? (
                            <div className="cron-ui-editor-panel">
                              <select
                                value={editState.mode}
                                onChange={(e) => setEditState({ ...editState, mode: e.target.value })}
                                className="cron-select-main"
                              >
                                <option value="interval">일정간격 마다</option>
                                <option value="daily">매일 특정시간</option>
                                <option value="weekly">매주 특정요일</option>
                              </select>

                              {editState.mode === 'interval' && (
                                <div className="editor-sub-row">
                                  <input
                                    type="number"
                                    min="1"
                                    value={editState.intervalValue}
                                    onChange={(e) => setEditState({ ...editState, intervalValue: parseInt(e.target.value, 10) })}
                                    className="input-number-val"
                                  />
                                  <select
                                    value={editState.intervalUnit}
                                    onChange={(e) => setEditState({ ...editState, intervalUnit: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    <option value="minute">분 마다</option>
                                    <option value="hour">시간 마다</option>
                                  </select>
                                </div>
                              )}

                              {editState.mode === 'daily' && (
                                <div className="editor-sub-row">
                                  <select
                                    value={editState.hour}
                                    onChange={(e) => setEditState({ ...editState, hour: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    {Array.from({ length: 24 }).map((_, h) => {
                                      const val = String(h).padStart(2, '0');
                                      return <option key={val} value={val}>{val}시</option>;
                                    })}
                                  </select>
                                  <select
                                    value={editState.minute}
                                    onChange={(e) => setEditState({ ...editState, minute: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    {Array.from({ length: 60 }).map((_, m) => {
                                      const val = String(m).padStart(2, '0');
                                      return <option key={val} value={val}>{val}분</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {editState.mode === 'weekly' && (
                                <div className="editor-sub-row">
                                  <select
                                    value={editState.dayOfWeek}
                                    onChange={(e) => setEditState({ ...editState, dayOfWeek: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    <option value="MON">월요일</option>
                                    <option value="TUE">화요일</option>
                                    <option value="WED">수요일</option>
                                    <option value="THU">목요일</option>
                                    <option value="FRI">금요일</option>
                                    <option value="SAT">토요일</option>
                                    <option value="SUN">일요일</option>
                                  </select>
                                  <select
                                    value={editState.hour}
                                    onChange={(e) => setEditState({ ...editState, hour: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    {Array.from({ length: 24 }).map((_, h) => {
                                      const val = String(h).padStart(2, '0');
                                      return <option key={val} value={val}>{val}시</option>;
                                    })}
                                  </select>
                                  <select
                                    value={editState.minute}
                                    onChange={(e) => setEditState({ ...editState, minute: e.target.value })}
                                    className="cron-select-sub"
                                  >
                                    {Array.from({ length: 60 }).map((_, m) => {
                                      const val = String(m).padStart(2, '0');
                                      return <option key={val} value={val}>{val}분</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              <div className="editor-btn-group">
                                <button onClick={() => saveEditedCron(sch)} className="btn-cron-save">적용</button>
                                <button onClick={() => setActiveEditingJob(null)} className="btn-cron-cancel">취소</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => startEditing(sch)} className="btn-edit-cron-trigger">
                              ⚙️ 주기 설정하기
                            </button>
                          )}
                        </td>
                        <td><code className="cron-code-text">{sch.cronExpression}</code></td>
                        <td>
                          <span className={`status-badge ${statuses[sch.jobName] && statuses[sch.jobName].startsWith('Running') ? 'status-running' : 'status-idle'}`}>
                            {statuses[sch.jobName] || '대기'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={sch.enabled}
                              onChange={(e) => updateSchedule(sch.jobName, sch.cronExpression, e.target.checked, sch.description)}
                            />
                            <span className="slider round"></span>
                          </label>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => triggerSchedule(sch.jobName)}
                            disabled={isBusy}
                            className="btn-trigger"
                          >
                            {isBusy ? '기동 중' : '즉시 실행'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* 달력 시각화 영역 */}
          <section className="admin-section">
            <div className="admin-section-head">
              <h2>예약된 스케줄 일정표 (시각화 달력)</h2>
              <div className="calendar-controls">
                <div className="calendar-nav-buttons">
                  <button onClick={() => {
                    const d = new Date(calendarAnchorDate);
                    if (calendarMode === 'day') d.setDate(d.getDate() - 1);
                    else if (calendarMode === 'week') d.setDate(d.getDate() - 7);
                    else d.setMonth(d.getMonth() - 1);
                    setCalendarAnchorDate(d);
                  }} className="btn-nav">◀</button>
                  <strong className="anchor-date-label">
                    {calendarMode === 'day' && calendarAnchorDate.toLocaleDateString()}
                    {calendarMode === 'week' && `${calendarAnchorDate.getFullYear()}년 ${calendarAnchorDate.getMonth() + 1}월 W${Math.ceil(calendarAnchorDate.getDate() / 7)}`}
                    {calendarMode === 'month' && `${calendarAnchorDate.getFullYear()}년 ${calendarAnchorDate.getMonth() + 1}월`}
                  </strong>
                  <button onClick={() => {
                    const d = new Date(calendarAnchorDate);
                    if (calendarMode === 'day') d.setDate(d.getDate() + 1);
                    else if (calendarMode === 'week') d.setDate(d.getDate() + 7);
                    else d.setMonth(d.getMonth() + 1);
                    setCalendarAnchorDate(d);
                  }} className="btn-nav">▶</button>
                  <button onClick={() => setCalendarAnchorDate(new Date())} className="btn-today">오늘</button>
                </div>
                <div className="period-tabs">
                  <button className={`period-tab-btn ${calendarMode === 'day' ? 'active' : ''}`} onClick={() => setCalendarMode('day')}>하루 일정</button>
                  <button className={`period-tab-btn ${calendarMode === 'week' ? 'active' : ''}`} onClick={() => setCalendarMode('week')}>주간 일정</button>
                  <button className={`period-tab-btn ${calendarMode === 'month' ? 'active' : ''}`} onClick={() => setCalendarMode('month')}>월간 달력</button>
                </div>
              </div>
            </div>

            {/* A. 하루 일정 (24시간 Timeline) */}
            {calendarMode === 'day' && (
              <div className="timeline-container">
                <div className="timeline-hours-header">
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} className="timeline-hour-col">
                      <span>{String(h).padStart(2, '0')}:00</span>
                    </div>
                  ))}
                </div>
                <div className="timeline-events-body">
                  {calendarEvents.length === 0 ? (
                    <div className="no-events-placeholder">오늘 예정된 수집 배치가 없습니다. 스케줄 사용 여부를 켜주세요.</div>
                  ) : (
                    calendarEvents.map((evt, idx) => {
                      const [hour, min] = evt.time.split(':').map(Number);
                      const percentLeft = ((hour * 60 + min) / 1440) * 100;
                      return (
                        <div
                          key={idx}
                          className="timeline-event-marker"
                          style={{ left: `${percentLeft}%` }}
                          title={`${evt.time} - ${evt.jobName}`}
                        >
                          <div className="marker-badge">{evt.jobName}</div>
                          <span className="marker-time">{evt.time}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* B. 주간 일정 (Weekly Grid) */}
            {calendarMode === 'week' && (
              <div className="weekly-grid-container">
                {['일', '월', '화', '수', '목', '금', '토'].map((dayName, idx) => {
                  const dayDate = new Date(calendarAnchorDate);
                  dayDate.setDate(calendarAnchorDate.getDate() - calendarAnchorDate.getDay() + idx);
                  const dateStr = dayDate.toISOString().split('T')[0];
                  const dayEvts = calendarEvents.filter(e => e.dateStr === dateStr);

                  return (
                    <div key={idx} className="weekly-day-column">
                      <div className="weekly-day-header">
                        <strong>{dayName}요일</strong>
                        <span>{dayDate.getDate()}일</span>
                      </div>
                      <div className="weekly-day-content">
                        {dayEvts.map((evt, eIdx) => (
                          <div key={eIdx} className="calendar-event-card">
                            <span className="evt-time">{evt.time}</span>
                            <strong className="evt-title">{evt.jobName}</strong>
                          </div>
                        ))}
                        {dayEvts.length === 0 && <span className="no-event-lbl">예정 없음</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* C. 월간 달력 (Monthly Grid) */}
            {calendarMode === 'month' && (
              <div className="monthly-calendar-container">
                <div className="month-grid-header">
                  {['일', '월', '화', '수', '목', '금', '토'].map(d => <div key={d} className="calendar-header-cell">{d}</div>)}
                </div>
                <div className="month-grid-cells">
                  {/* 시작요일 이전의 공백 셀 */}
                  {Array.from({ length: firstDayIndex }).map((_, idx) => (
                    <div key={`empty-${idx}`} className="calendar-cell empty"></div>
                  ))}
                  {/* 날짜 셀들 */}
                  {Array.from({ length: daysInMonth }).map((_, dIdx) => {
                    const dayNum = dIdx + 1;
                    const dateObj = new Date(yearStr, monthStr, dayNum);
                    const dateStr = dateObj.toISOString().split('T')[0];
                    const dayEvts = calendarEvents.filter(e => e.dateStr === dateStr);

                    return (
                      <div key={dayNum} className="calendar-cell">
                        <span className="cell-day-num">{dayNum}</span>
                        <div className="cell-events-container">
                          {dayEvts.map((evt, eIdx) => (
                            <div key={eIdx} className="calendar-event-dot" title={`${evt.time} - ${evt.jobName}`}>
                              <strong>{evt.time}</strong> {evt.jobName}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {/* 2️⃣ 수집 실행 로그 조회 메인 탭 */}
      {activeMainTab === 'logs' && (
        <section className="admin-section">
          <div className="admin-section-head">
            <h2>수집 실행 로그 상세 조회</h2>
            <span>특정 날짜 및 수집 유형에 맞춰 수집 기동 내역과 원장 로그를 분석합니다.</span>
          </div>

          {/* 🔍 검색 필터 조건 조작기 */}
          <div className="log-search-filter-bar">
            <div className="filter-group">
              <label>실행 날짜 검색</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="filter-date-input"
              />
            </div>
            <div className="filter-group">
              <label>수집 유형</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select-input"
              >
                <option value="">전체 유형</option>
                <option value="sync">sync (증분동기화)</option>
                <option value="kopis-sync">kopis-sync (코피스)</option>
                <option value="EV01">EV01 (공공 축제)</option>
                <option value="enrich-EV01">enrich-EV01 (상세보강)</option>
                <option value="EV02">EV02 (KOPIS 공연)</option>
                <option value="EV03">EV03 (관광공사)</option>
                <option value="seoul-event">seoul-event (서울)</option>
                <option value="gyeonggi-event">gyeonggi-event (경기)</option>
                <option value="ifac-event">ifac-event (인천)</option>
                <option value="tourist">tourist (관광지)</option>
                <option value="std">std (표준데이터)</option>
              </select>
            </div>
            <button onClick={() => fetchFilteredLogs(0)} className="btn-search-logs">
              🔍 검색하기
            </button>
          </div>

          {/* 로그 그리드 & 상세 로그 분할 뷰 */}
          <div className="admin-log-layout">
            <div className="admin-log-grid-container">
              <table className="admin-grid-table table-logs">
                <thead>
                  <tr>
                    <th>유형</th>
                    <th>상태</th>
                    <th>시작 일시</th>
                    <th>소요 시간</th>
                    <th>조회</th>
                    <th>저장</th>
                    <th>스킵</th>
                    <th>에러</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        검색 조건에 맞는 수집 로그 내역이 존재하지 않습니다.
                      </td>
                    </tr>
                  ) : logs.map(log => {
                    const isSelected = selectedLog?.id === log.id;
                    const duration = log.endAt && log.startAt
                      ? (new Date(log.endAt).getTime() - new Date(log.startAt).getTime()) / 1000
                      : null;

                    return (
                      <tr
                        key={log.id}
                        className={`log-row-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => openLog(log)}
                      >
                        <td><strong className="collect-type-badge">{log.collectType}</strong></td>
                        <td>
                          <span className={`log-status-badge status-${String(log.status).toLowerCase()}`}>
                            {log.status}
                          </span>
                        </td>
                        <td><span className="log-time-text">{formatDateTime(log.startAt)}</span></td>
                        <td>{formatDuration(duration)}</td>
                        <td>{number(log.totalFetched)}</td>
                        <td className="text-primary font-bold">{number(log.totalSaved)}</td>
                        <td className="text-muted">{number(log.totalSkipped)}</td>
                        <td className={log.errorCount > 0 ? 'text-danger font-bold' : 'text-muted'}>
                          {number(log.errorCount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* 페이지네이션 */}
              {logTotal > 25 && (
                <div className="log-pagination-bar">
                  <button disabled={logPage === 0} onClick={() => fetchFilteredLogs(logPage - 1)}>이전</button>
                  <span>페이지 {logPage + 1} / {Math.ceil(logTotal / 25)}</span>
                  <button disabled={(logPage + 1) * 25 >= logTotal} onClick={() => fetchFilteredLogs(logPage + 1)}>다음</button>
                </div>
              )}
            </div>

            {/* 원장 로그 라인 상세 출력 */}
            <div className="admin-log-detail">
              {selectedLog ? (
                <>
                  <div className="log-detail-header">
                    <h3>{selectedLog.collectType} 수집 콘솔 로그</h3>
                    <span className="log-detail-meta">로그 ID: #{selectedLog.id} | {selectedLog.resultMsg || selectedLog.apiEndpoint}</span>
                  </div>
                  <div className="admin-log-lines">
                    {logLines.length === 0 ? (
                      <div className="admin-placeholder">해당 수집 이력의 하위 로그 메세지 라인이 비어있습니다.</div>
                    ) : logLines.map((line, index) => (
                      <div key={line.id || index} className={`admin-log-line level-${String(line.logLevel || '').toLowerCase()}`}>
                        <span>{formatDateTime(line.logTime)}</span>
                        <strong>{line.logLevel}</strong>
                        <p>{line.message}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="admin-placeholder">좌측 목록의 특정 수집 이력 행을 클릭하면 이곳에 개별 상세 콘솔 로그 메시지가 정밀 노출됩니다.</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 3️⃣ 사용자 행동 분석 및 통계 탭 */}
      {activeMainTab === 'analytics' && (
        <section className="admin-section">
          <div className="admin-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>사용자 통계 및 실시간 여정(Telemetry)</h2>
              <span>방문자들의 체류 시간, 인기 축제/검색어, 그리고 개별 사용자 활동 타임라인을 정밀 추적합니다.</span>
            </div>
            <button 
              onClick={handleRefreshAnalytics}
              style={{
                background: '#4b5563',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#374151'}
              onMouseOut={e => e.currentTarget.style.background = '#4b5563'}
            >
              🔄 실시간 통계 새로고침
            </button>
          </div>

          {/* 📊 통계 대시보드 요약 그리드 */}
          {analyticsSummary && (
            <div className="analytics-summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="admin-stat" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px' }}>
                <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>누적 방문 세션 수</span>
                <strong style={{ fontSize: '28px', display: 'block', marginTop: '8px', color: '#15803d' }}>
                  {number(analyticsSummary.overall?.total_sessions || 0)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>명</span>
                </strong>
              </div>
              <div className="admin-stat" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
                <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>평균 체류 시간</span>
                <strong style={{ fontSize: '28px', display: 'block', marginTop: '8px', color: '#1d4ed8' }}>
                  {number(analyticsSummary.overall?.avg_duration || 0)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>초</span>
                </strong>
              </div>
              <div className="admin-stat" style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '12px', padding: '16px' }}>
                <span style={{ fontSize: '12px', color: '#db2777', fontWeight: 600 }}>총 이벤트/클릭 수</span>
                <strong style={{ fontSize: '28px', display: 'block', marginTop: '8px', color: '#be185d' }}>
                  {number(analyticsSummary.overall?.total_events || 0)} <span style={{ fontSize: '14px', fontWeight: 'normal' }}>액션</span>
                </strong>
              </div>
            </div>
          )}

          {/* 📈 차트 & 집계 위젯 패널 */}
          {analyticsSummary && (
            <div className="analytics-dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
              
              {/* 일별 트렌드 차트 */}
              <div className="analytics-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>📅 일별 방문 추이 (최근 7일)</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', height: '140px', gap: '8px', paddingTop: '10px' }}>
                  {(analyticsSummary.daily || []).map((day, idx) => {
                    const maxVal = Math.max(...analyticsSummary.daily.map(d => d.session_count || 1), 1);
                    const pct = ((day.session_count || 0) / maxVal) * 100;
                    return (
                      <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: '10px', color: '#374151', marginBottom: '4px', fontWeight: 'bold' }}>{day.session_count}</span>
                        <div style={{ width: '100%', height: `${pct}%`, background: 'linear-gradient(180deg, #3b82f6, #60a5fa)', borderRadius: '4px 4px 0 0', minHeight: '4px' }}></div>
                        <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '8px', whiteSpace: 'nowrap' }}>{day.visit_date?.substring(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 기능별 점유율 */}
              <div className="analytics-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>⚙️ 기능별 사용 빈도</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(analyticsSummary.eventDistribution || []).map((evt, idx) => {
                    const totalEvts = analyticsSummary.eventDistribution.reduce((acc, c) => acc + (c.event_count || 0), 0) || 1;
                    const pct = (((evt.event_count || 0) / totalEvts) * 100).toFixed(1);
                    return (
                      <div key={idx}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 500, color: '#374151' }}>{evt.event_type}</span>
                          <span style={{ color: '#6b7280' }}>{number(evt.event_count)}건 ({pct}%)</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#7b3fe4', borderRadius: '4px' }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 실시간 인기 축제 Top 5 */}
              <div className="analytics-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>🔥 최다 상세 조회 축제 (Top 5)</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(analyticsSummary.popularFestivals || []).map((fest, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fafafa', borderRadius: '8px', fontSize: '12px' }}>
                      <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75%' }}>
                        <span style={{ color: '#e85d2f', fontWeight: 'bold', marginRight: '6px' }}>{idx + 1}</span> {fest.target_name || fest.target_id}
                      </span>
                      <strong style={{ color: '#e85d2f' }}>{fest.view_count}회</strong>
                    </div>
                  ))}
                </div>
              </div>

              {/* 인기 검색 키워드 Top 5 */}
              <div className="analytics-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>🔍 실시간 인기 검색어 (Top 5)</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(analyticsSummary.popularKeywords || []).map((kw, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#f3f4f6', borderRadius: '20px', fontSize: '12px' }}>
                      <span style={{ color: '#4b5563', fontWeight: 500 }}>#{kw.keyword}</span>
                      <strong style={{ color: '#2563eb' }}>{kw.search_count}</strong>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* 🔍 세션 리스트 필터 바 */}
          <div className="log-search-filter-bar" style={{ marginBottom: '16px' }}>
            <div className="filter-group">
              <label>세션 접속일 검색</label>
              <input
                type="date"
                value={analyticsSessionDate}
                onChange={(e) => setAnalyticsSessionDate(e.target.value)}
                className="filter-date-input"
              />
            </div>
            <div className="filter-group">
              <label>접속 기기</label>
              <select
                value={analyticsSessionDevice}
                onChange={(e) => setAnalyticsSessionDevice(e.target.value)}
                className="filter-select-input"
              >
                <option value="all">전체 기기</option>
                <option value="mobile">모바일</option>
                <option value="desktop">데스크톱(PC)</option>
              </select>
            </div>
            <button onClick={() => fetchAnalyticsSessions(0)} className="btn-search-logs">
              🔍 세션 조회
            </button>
          </div>

          {/* 👥 세션 목록 & 여정 드릴다운 타임라인 분할 뷰 */}
          <div className="admin-log-layout">
            
            {/* 세션 목록 그리드 */}
            <div className="admin-log-grid-container" style={{ flex: 1.4 }}>
              <table className="admin-grid-table table-logs">
                <thead>
                  <tr>
                    <th>세션 ID</th>
                    <th>IP 식별자</th>
                    <th>체류 시간</th>
                    <th>접속 일시</th>
                    <th>최종 활동</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsSessions.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        선택하신 조건의 사용자 세션 내역이 존재하지 않습니다.
                      </td>
                    </tr>
                  ) : analyticsSessions.map(sess => {
                    const isSelected = selectedSession?.id === sess.id;
                    const isMobile = /mobi|android|iphone|ipad/i.test(sess.userAgent || '');
                    return (
                      <tr
                        key={sess.id}
                        className={`log-row-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => fetchSessionEvents(sess)}
                      >
                        <td><span className="log-time-text" style={{ fontFamily: 'monospace' }}>{sess.id?.substring(0, 8)}...</span></td>
                        <td><span style={{ fontSize: '11px', color: '#6b7280' }}>{sess.ipHash}</span></td>
                        <td>
                          <span style={{ color: sess.durationSeconds > 60 ? '#16a34a' : '#6b7280', fontWeight: sess.durationSeconds > 60 ? 'bold' : 'normal' }}>
                            {sess.durationSeconds}초 ({isMobile ? '📱 모바일' : '💻 PC'})
                          </span>
                        </td>
                        <td><span className="log-time-text">{formatDateTime(sess.startAt)}</span></td>
                        <td><span className="log-time-text">{formatDateTime(sess.endAt)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* 세션 페이지네이션 */}
              {analyticsSessionTotal > 15 && (
                <div className="log-pagination-bar">
                  <button disabled={analyticsSessionPage === 0} onClick={() => fetchAnalyticsSessions(analyticsSessionPage - 1)}>이전</button>
                  <span>페이지 {analyticsSessionPage + 1} / {Math.ceil(analyticsSessionTotal / 15)}</span>
                  <button disabled={(analyticsSessionPage + 1) * 15 >= analyticsSessionTotal} onClick={() => fetchAnalyticsSessions(analyticsSessionPage + 1)}>다음</button>
                </div>
              )}
            </div>

            {/* 사용자 여정 타임라인 상세 로그 (드릴다운) */}
            <div className="admin-log-detail" style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column' }}>
              {selectedSession ? (
                <>
                  <div className="log-detail-header" style={{ marginBottom: '16px', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold' }}>👤 사용자 여정 (User Journey Timeline)</h3>
                    <span className="log-detail-meta" style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginTop: '4px', wordBreak: 'break-all' }}>
                      세션: {selectedSession.id} <br />
                      기기: {selectedSession.userAgent}
                    </span>
                  </div>
                  
                  {loadingSessionEvents ? (
                    <div className="admin-placeholder">상세 활동 이벤트를 로딩 중입니다...</div>
                  ) : (
                    <div className="admin-log-lines" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', paddingRight: '8px' }}>
                      {sessionEvents.length === 0 ? (
                        <div className="admin-placeholder">기록된 이벤트 활동이 없습니다.</div>
                      ) : sessionEvents.map((evt, index) => {
                        let badgeColor = '#6b7280';
                        let badgeBg = '#f3f4f6';
                        let icon = '📍';
                        if (evt.eventType === 'VIEW_DETAIL') {
                          badgeColor = '#e85d2f';
                          badgeBg = '#fff0eb';
                          icon = '🔍';
                        } else if (evt.eventType === 'SEARCH') {
                          badgeColor = '#2563eb';
                          badgeBg = '#eff6ff';
                          icon = '⌨️';
                        } else if (evt.eventType === 'PAGE_VIEW') {
                          badgeColor = '#16a34a';
                          badgeBg = '#f0fdf4';
                          icon = '🏠';
                        }

                        return (
                          <div key={evt.id || index} style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                            {/* 타임라인 축선 처리 */}
                            {index < sessionEvents.length - 1 && (
                              <div style={{ position: 'absolute', left: '11px', top: '24px', bottom: '-20px', width: '2px', background: '#e5e7eb' }}></div>
                            )}
                            
                            {/* 노드 아이콘 */}
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: badgeBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', border: `1px solid ${badgeColor}`, zIndex: 1 }}>
                              {icon}
                            </div>
                            
                            {/* 노드 바디 */}
                            <div style={{ flex: 1, background: '#fafafa', borderRadius: '8px', padding: '10px 12px', border: '1px solid #f3f4f6' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: badgeColor, textTransform: 'uppercase' }}>
                                  {evt.eventType}
                                </span>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                                  {formatDateTime(evt.createdAt)?.substring(11)}
                                </span>
                              </div>
                              <p style={{ fontSize: '12px', margin: 0, fontWeight: 500, color: '#374151' }}>
                                {evt.targetId} {evt.targetName ? `(${evt.targetName})` : ''}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="admin-placeholder" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
                  <span style={{ fontSize: '32px' }}>👥</span>
                  <span>왼쪽 세션 목록에서 세션을 선택하면 해당 사용자의 정밀 활동 타임라인이 타임라인 드릴다운 형태로 노출됩니다.</span>
                </div>
              )}
            </div>

          </div>
        </section>
      )}

      {/* 기본 데이터 요약 (하단 고정) */}
      <div className="admin-two-col">
        <section className="admin-section">
          <div className="admin-section-head">
            <h2>출처별 데이터 분포</h2>
          </div>
          <div className="admin-source-list">
            {(stats?.eventsBySource || []).map(row => (
              <div key={row.source} className="admin-source-row">
                <span>{row.source}</span>
                <strong>{number(row.count)}건</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <h2>스케줄 통계 대시보드 요약 (전체)</h2>
          </div>
          <div className="admin-stats-summary-footer">
            <div className="footer-stat-box">
              <span>수집 성공률</span>
              <strong>
                {stats?.collectLogsByStatus ? (
                  ((stats.collectLogsByStatus.find(s => s.status === 'success')?.count || 0) /
                  (stats.collectLogsByStatus.reduce((acc, curr) => acc + curr.count, 0) || 1) * 100).toFixed(1)
                ) : 0}%
              </strong>
            </div>
            <div className="footer-stat-box">
              <span>누적 축제/행사 건수</span>
              <strong>{number((stats?.festivals || 0) + (stats?.events || 0))}건</strong>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function Stat({ label, value, tone }) {
  return (
    <div className={`admin-stat ${tone || ''}`}>
      <span>{label}</span>
      <strong>{number(value)}</strong>
    </div>
  );
}

export default AdminApp;
